import { useCallback, useState, useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import qs from 'qs'
import { rem } from 'polished'
import setRegionCookie from 'cookies/setRegionCookie'
import { setGeoRedirect, Status } from 'storage/geo'
import GeoContext from 'contexts/geoContext'
import { formToObject } from 'lib/forms/formToObject'
import { getCreditRebookingDetails } from 'api/flights'
import { pushWithRegion } from 'actions/NavigationActions'

import { useAppDispatch } from 'hooks/reduxHooks'
import { setFlightCreditData } from 'actions/FlightActions'

import TextInput from 'components/Common/Form/Input/TextInput'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import RebookingError from 'components/Flights/CreditRebooking/RebookingError'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import type { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router'

const InputField = styled(TextInput)`
  span {
    &:first-child {
      font-weight: ${props => props.theme.font.primary.weight.bold};
    }
  }

  span[class^='InputWrap__StyledInputWrap'] {
    border-radius: ${props => props.theme.borderRadius.S};
    height: ${rem(48)};
    margin-top: ${rem(4)};
  }
`

const Loading = styled(LoadingIndicator)`
  padding: ${rem(12)};
`

const StyledForm = styled.form`
  > * {
    margin-top: ${rem(24)};
  }
`

interface CreditRebookingForm {
  email: string
  bookingReference: string
}
interface Props extends RouteComponentProps<{}> {
  isOpen: boolean
}

function RebookingModalContent({ isOpen, location }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<{ status: number, message: string } | null>(null)
  const { currentRegionCode } = useContext(GeoContext)

  const validationErrorMessage = intl.formatMessage({
    id: '8jB5xbcscYrV8pQkwF7Z',
    defaultMessage: 'Error validating your booking details',
    description: 'Error message when flight credit booking validation fails',
  })

  const getBookingDetails = useCallback((email: string, bookingReference: string) => {
    setLoading(true)

    getCreditRebookingDetails(email, bookingReference).then(res => {
      setLoading(false)
      const { status, message, creditDetails } = res

      if (status === 200) {
        const queryParams = {
          ...creditDetails.passengerCounts,
          flightsCredit: true,
        }

        if (currentRegionCode !== creditDetails.region) {
          setRegionCookie(creditDetails.region)
          setGeoRedirect(Status.redirected)
          window.location.href = location.pathname.replace(/^\/[a-z]+/, '/' + creditDetails.region.toLowerCase()) + location.search
        } else {
          dispatch(setFlightCreditData(res.creditDetails))
          dispatch(pushWithRegion('/flights', qs.stringify(queryParams)))
        }
      } else {
        setError({ status, message })
      }
    }).catch(error => {
      setLoading(false)
      setError({
        status: 500,
        message: validationErrorMessage,
      })
      throw error
    })
  }, [currentRegionCode, location.pathname, location.search, dispatch, validationErrorMessage])

  const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const { email, bookingReference } = formToObject<CreditRebookingForm>(e.currentTarget)
    getBookingDetails(email, bookingReference)
  }, [getBookingDetails])

  const modalTitle = intl.formatMessage({
    id: 'AKNsM9IT6PDc4Vrch3E4',
    defaultMessage: 'Verify your booking',
    description: 'Modal title for flight credit rebooking verification',
  })

  const modalSubtitle = intl.formatMessage({
    id: '-VwAWyVNKS7BHLbmUYG7',
    defaultMessage: 'Please enter your Luxury Escapes account email address linked to your flight credit booking.',
    description: 'Modal subtitle explaining what information to enter for flight credit rebooking',
  })

  const emailLabel = intl.formatMessage({
    id: '9PwmIlnmrKF4PN0qF9FH',
    defaultMessage: 'Email address',
    description: 'Label for email input field in flight credit rebooking form',
  })

  const emailPlaceholder = intl.formatMessage({
    id: 'iUxsdONXOYbto8toykZI',
    defaultMessage: 'Enter email address',
    description: 'Placeholder for email input field in flight credit rebooking form',
  })

  const bookingRefLabel = intl.formatMessage({
    id: 'dD4qIt3Kb-zwuAtClUHz',
    defaultMessage: 'Booking reference',
    description: 'Label for booking reference input field in flight credit rebooking form',
  })

  const bookingRefPlaceholder = intl.formatMessage({
    id: 'abR-J2L-Yj8RSFH4kasE',
    defaultMessage: 'Enter booking reference',
    description: 'Placeholder for booking reference input field in flight credit rebooking form',
  })

  const continueLabel = intl.formatMessage({
    id: 'HnXYFhh758S74VyCWmgt',
    defaultMessage: 'Continue',
    description: 'Submit button text for flight credit rebooking verification form',
  })

  return (
    <ModalBase isOpen={isOpen}>
      <StyledForm name="joinForm" onSubmit={onSubmit}>
        <ModalHeader
          title={modalTitle}
          subtitle={modalSubtitle}
        />
        <ModalBody>
          <ModalContent>
            <InputField
              required
              name="email"
              type="email"
              inputMode="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              data-testid="credit-reservation-email"
            />
            <InputField
              required
              noValidationSpacing
              name="bookingReference"
              label={bookingRefLabel}
              placeholder={bookingRefPlaceholder}
              data-testid="credit-reservation-booking"
            />
            {loading && <Loading inline />}
            {error && !loading && <RebookingError error={error} />}
          </ModalContent>
        </ModalBody>
        <ModalFooter
          primaryActionProps={{
            children: continueLabel,
            type: 'submit',
            disabled: loading,
          }}
        />
      </StyledForm>
    </ModalBase>
  )
}

export default withRouter(RebookingModalContent)
